import React, { useState } from 'react';
import ReactDOM from 'react-dom';

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@mui/material';

// Token storage key
const TOKEN_STORAGE_KEY = 'anundi_auth_token';

interface TokenModalProps {
  onSubmit: (token: string) => void;
  onClose: () => void;
}

// Component implementation
const TokenModalComponent: React.FC<TokenModalProps> = ({
  onSubmit,
  onClose,
}) => {
  const [token, setToken] = useState('');
  const [rememberToken, setRememberToken] = useState(true);

  const handleSubmit = () => {
    if (token.trim()) {
      // Save token to localStorage if remember is checked
      if (rememberToken) {
        localStorage.setItem(TOKEN_STORAGE_KEY, token);
      }
      onSubmit(token);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Authentication Required</DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <TextField
            autoFocus
            label="Authentication Token"
            type="password"
            fullWidth
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyPress={handleKeyPress}
            margin="normal"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberToken}
                onChange={(e) => setRememberToken(e.target.checked)}
                color="primary"
              />
            }
            label="Remember token for future uploads"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!token.trim()}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Function to clear the saved token
export const clearSavedToken = (): void => {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
};

// Function to show the modal and return a promise with the token
export const showTokenModal = (): Promise<string | null> => {
  // First try to get token from localStorage
  const savedToken = localStorage.getItem(TOKEN_STORAGE_KEY);

  if (savedToken) {
    console.log('Using saved authentication token');
    return Promise.resolve(savedToken);
  }

  // If no token in storage, show the modal
  return new Promise((resolve) => {
    const modalRoot = document.createElement('div');
    document.body.appendChild(modalRoot);

    const handleClose = () => {
      ReactDOM.unmountComponentAtNode(modalRoot);
      document.body.removeChild(modalRoot);
      resolve(null);
    };

    const handleSubmit = (token: string) => {
      ReactDOM.unmountComponentAtNode(modalRoot);
      document.body.removeChild(modalRoot);
      resolve(token);
    };

    ReactDOM.render(
      <TokenModalComponent onClose={handleClose} onSubmit={handleSubmit} />,
      modalRoot
    );
  });
};

export default TokenModalComponent;
